import React, { useState } from "react";
import { Button } from "@material-ui/core";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LanguageIcon from "@mui/icons-material/Language";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import cleanStorageBeforeSearch from "../../../../utils/cleanStorageBeforeSearch";
import { useGlobal } from "../../../../contexts/globalContext";
import formatActivityPrice, { activityPriceInfo } from "../../../../utils/formatActivityPrice";
import decodeHtmlEntities from "../../../../utils/decodeHtmlEntities";

const useStyles = makeStyles((theme) => ({
	card: {
		fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		background: "#fff",
		borderRadius: 12,
		overflow: "hidden",
		display: "flex",
		width: "100%",
		minHeight: 170,
		boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
		border: "1px solid #ebebeb",
		cursor: "pointer",
		transition: "box-shadow 0.2s",
		"&:hover": { boxShadow: "0 4px 18px rgba(0,0,0,0.12)" },
		[theme.breakpoints.down("sm")]: { flexDirection: "column" },
	},
	imageWrapper: {
		width: 220,
		minWidth: 220,
		flexShrink: 0,
		overflow: "hidden",
		[theme.breakpoints.down("sm")]: { width: "100%", minWidth: "unset", height: 180 },
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		display: "block",
	},
	imagePlaceholder: {
		width: "100%",
		height: "100%",
		minHeight: 170,
		background: "#f0f0f0",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#bbb",
		fontSize: 12,
	},
	body: {
		flex: 1,
		padding: "16px 20px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		minWidth: 0,
	},
	cancelText: {
		fontSize: 10,
		fontWeight: 700,
		color: "#2e7d32",
		letterSpacing: 1,
		textTransform: "uppercase",
		marginBottom: 5,
	},
	title: {
		fontSize: 17,
		fontWeight: 700,
		color: "#1a1a1a",
		lineHeight: 1.3,
		marginBottom: 5,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	ratingRow: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		marginBottom: 6,
	},
	ratingIcon: { fontSize: "14px !important", color: theme.palette.primary.main },
	ratingScore: {
		fontSize: 13,
		fontWeight: 700,
		color: theme.palette.primary.main,
	},
	ratingCount: {
		fontSize: 12,
		color: "#999",
	},
	description: {
		fontSize: 13,
		color: "#666",
		lineHeight: 1.55,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		marginBottom: 10,
	},
	metaRow: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 16,
		marginBottom: 6,
	},
	metaItem: {
		display: "flex",
		alignItems: "center",
		gap: 5,
		fontSize: 11,
		fontWeight: 600,
		color: "#555",
		textTransform: "uppercase",
		letterSpacing: 0.4,
	},
	metaIcon: { fontSize: "14px !important", color: "#888" },
	voucherRow: {
		display: "flex",
		alignItems: "center",
		gap: 5,
		fontSize: 11,
		fontWeight: 600,
		color: "#555",
		textTransform: "uppercase",
		letterSpacing: 0.4,
		marginTop: 4,
	},
	sidebar: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: "16px 20px",
		minWidth: 150,
		gap: 10,
		borderLeft: "1px solid #f0f0f0",
		[theme.breakpoints.down("sm")]: {
			flexDirection: "row",
			borderLeft: "none",
			borderTop: "1px solid #f0f0f0",
			justifyContent: "space-between",
		},
	},
	priceBlock: { textAlign: "center" },
	priceLabel: {
		fontSize: 10,
		color: "#aaa",
		textTransform: "uppercase",
		letterSpacing: 0.8,
		marginBottom: 2,
	},
	priceValue: {
		fontSize: 24,
		fontWeight: 800,
		color: theme.palette.primary.main,
		lineHeight: 1.1,
	},
	priceSuffix: {
		fontSize: 15,
		fontWeight: 700,
	},
	ctaBtn: {
		borderRadius: 8,
		fontWeight: 700,
		textTransform: "uppercase",
		fontSize: 11,
		letterSpacing: 0.5,
		padding: "8px 16px",
		whiteSpace: "nowrap",
		width: "100%",
	},
}));

const LANG_NAMES = {
	pt: "Português", en: "Inglês", es: "Espanhol", fr: "Francês",
	de: "Alemão", it: "Italiano", zh: "Chinês", ja: "Japonês",
};

// Função removida - agora usando decodeHtmlEntities do utils

const getActivityImage = (activity) => {
	// Civitatis: photos = { gallery: [{paths:{big,original,thumbnail},...}], header: [...] }
	const p = activity.photos;
	if (p) {
		const gallery = p.gallery || p.header;
		if (Array.isArray(gallery) && gallery.length) {
			const paths = gallery[0].paths;
			if (paths) return paths.original || paths.big || paths.thumbnail;
		}
	}
	// fallback for flat arrays or other shapes
	const first = activity.images?.[0];
	if (first) {
		if (typeof first === "string") return first;
		return first.url || first.src || null;
	}
	return activity.image || activity.imageUrl || activity.mainImage || null;
};

const CivitatisCard = ({ activity }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useGlobal();
	const [imgError, setImgError] = useState(false);

	const id = activity.id || activity.activityId;
	const title = decodeHtmlEntities(activity.title || activity.name || `Atividade ${id}`);
	const imageUrl = getActivityImage(activity);
	const cleanDescription = decodeHtmlEntities(activity.description || activity.shortDescription || "");
	const hasFreeCancellation = Array.isArray(activity.cancelPolicies)
		? activity.cancelPolicies.some((p) => p.penalty === 0 || p.penalty === 0.0)
		: activity.daysBeforeCancellationPolicy > 0;
	const rating = activity.score ?? activity.rating ?? null;
	const reviewCount = activity.reviews || activity.numComments || activity.reviewCount || null;
	const price = formatActivityPrice(activity, activityPriceInfo(user));

	const durationMin = activity.duration?.min;
	const durationDisplay = durationMin
		? durationMin >= 60
			? `${Math.round(durationMin / 60)} hora${Math.round(durationMin / 60) !== 1 ? "s" : ""}`
			: `${durationMin} min`
		: null;

	const mainLang = activity.guideLanguages?.[0];
	const langDisplay = mainLang ? (LANG_NAMES[mainLang] || mainLang.toUpperCase()) : null;

	const handleClick = () => {
		const currentData = JSON.parse(sessionStorage.getItem("entertainmentSearch") || "{}");
		cleanStorageBeforeSearch();
		sessionStorage.setItem("entertainmentSearch", JSON.stringify(currentData));
		sessionStorage.setItem("buscaAtual", "Entretenimento");
		sessionStorage.setItem(
			"@EntertainmentSelected",
			JSON.stringify({ activityId: id, refToken: activity.refToken, activity })
		);
		history.push("/busca/entertainment-details");
	};

	return (
		<div className={classes.card} onClick={handleClick}>
			{/* Image */}
			<div className={classes.imageWrapper}>
				{imageUrl && !imgError ? (
					<img
						src={imageUrl}
						alt={title}
						className={classes.image}
						onError={() => setImgError(true)}
					/>
				) : (
					<div className={classes.imagePlaceholder}>Sem imagem</div>
				)}
			</div>

			{/* Body */}
			<div className={classes.body}>
				<div>
					{hasFreeCancellation && (
						<p className={classes.cancelText}>Cancelamento Gratuito</p>
					)}

					<p className={classes.title}>{title}</p>

					{rating !== null && (
						<div className={classes.ratingRow}>
							<StarIcon className={classes.ratingIcon} />
							<span className={classes.ratingScore}>{Number(rating).toFixed(1)}/10</span>
							{reviewCount && (
								<span className={classes.ratingCount}>({reviewCount} opiniões)</span>
							)}
						</div>
					)}

					{cleanDescription && (
						<p className={classes.description}>{cleanDescription}</p>
					)}
				</div>

				<div>
					{(durationDisplay || langDisplay) && (
						<div className={classes.metaRow}>
							{durationDisplay && (
								<span className={classes.metaItem}>
									<AccessTimeIcon className={classes.metaIcon} />
									{durationDisplay}
								</span>
							)}
							{langDisplay && (
								<span className={classes.metaItem}>
									<LanguageIcon className={classes.metaIcon} />
									{langDisplay}
								</span>
							)}
						</div>
					)}
					<div className={classes.voucherRow}>
						<ConfirmationNumberOutlinedIcon className={classes.metaIcon} />
						Voucher Eletrônico
					</div>
				</div>
			</div>

			{/* Right: price + CTA */}
			<div className={classes.sidebar}>
				<div className={classes.priceBlock}>
					<p className={classes.priceLabel}>A partir de</p>
					{price ? (
						<p className={classes.priceValue}>
							{price.amount}
							{price.suffix && <span className={classes.priceSuffix}> {price.suffix}</span>}
						</p>
					) : (
						<p className={classes.priceValue} style={{ fontSize: 15, color: "#aaa" }}>
							Consultar
						</p>
					)}
				</div>
				<Button
					variant="contained"
					color="primary"
					className={classes.ctaBtn}
					onClick={(e) => { e.stopPropagation(); handleClick(); }}
				>
					Ver Detalhes
				</Button>
			</div>
		</div>
	);
};

export default CivitatisCard;
